/** A `minoseg_osszesito` adatbázis-függvény válaszának alakja (terv: 3. feladat) és futásidejű ellenőrzése. */
export class MinosegAlakHiba extends Error {}

export interface MinosegIrodaSor {
  office_id: string;
  nev: string;
  kikuldve: number;
  valaszolt: number;
  valaszarany: number | null;
  kapcsolat_igen_arany: number | null;
  szemle_latogato: number;
  szemle_crm: number;
  megbizas_latogato: number;
  megbizas_crm: number;
  elteres_osszes: number;
  elteres_nyitott: number;
  megbizas_rejtett: number;
  elegedettseg_atlag: number | null;
}
export interface MinosegValaszok {
  kapcsolat: string | null;
  szemle: string | null;
  megbizas: string | null;
  elegedettseg: number | null;
  megjegyzes: string | null;
}
export interface MinosegElteres {
  id: string;
  lead_id: string;
  office_id: string;
  iroda_nev: string;
  kuldve_at: string;
  valaszolt_at: string | null;
  crm_statusz_kuldeskor: string;
  crm_elert_rang_kuldeskor: number;
  crm_elert_rang_valaszkor: number | null;
  crm_statusz_most: string;
  crm_elert_rang_most: number;
  valaszok: MinosegValaszok;
  elteresek: string[];
  elteres_allapot: string | null;
  elteres_megjegyzes: string | null;
  elteres_kezelve_at: string | null;
}
export interface MinosegValasz {
  idoszak: { tol: string; ig: string };
  irodak: MinosegIrodaSor[];
  elteresek: MinosegElteres[];
}

type Kind = 'szam' | 'szam?' | 'szoveg' | 'szoveg?' | 'szovegtomb';
type Alak = { [kulcs: string]: Kind | Alak | [Alak] };

const ALAK: Alak = {
  idoszak: { tol: 'szoveg', ig: 'szoveg' },
  irodak: [{
    office_id: 'szoveg', nev: 'szoveg', kikuldve: 'szam', valaszolt: 'szam', valaszarany: 'szam?', kapcsolat_igen_arany: 'szam?',
    szemle_latogato: 'szam', szemle_crm: 'szam', megbizas_latogato: 'szam', megbizas_crm: 'szam',
    elteres_osszes: 'szam', elteres_nyitott: 'szam', megbizas_rejtett: 'szam', elegedettseg_atlag: 'szam?',
  }],
  elteresek: [{
    id: 'szoveg', lead_id: 'szoveg', office_id: 'szoveg', iroda_nev: 'szoveg', kuldve_at: 'szoveg', valaszolt_at: 'szoveg?',
    crm_statusz_kuldeskor: 'szoveg', crm_elert_rang_kuldeskor: 'szam', crm_elert_rang_valaszkor: 'szam?',
    crm_statusz_most: 'szoveg', crm_elert_rang_most: 'szam',
    valaszok: { kapcsolat: 'szoveg?', szemle: 'szoveg?', megbizas: 'szoveg?', elegedettseg: 'szam?', megjegyzes: 'szoveg?' },
    elteresek: 'szovegtomb', elteres_allapot: 'szoveg?', elteres_megjegyzes: 'szoveg?', elteres_kezelve_at: 'szoveg?',
  }],
};

function leiro(x: unknown): string {
  return x === null ? 'null' : Array.isArray(x) ? 'tömb' : typeof x;
}

const elorag = 'A minőségi mutatók válasza nem a várt alakú';

function ellenorzes(x: unknown, alak: Kind | Alak | [Alak], ut: string): void {
  if (typeof alak === 'string') {
    if (alak === 'szovegtomb') {
      if (!Array.isArray(x)) throw new MinosegAlakHiba(`${elorag}: a(z) ${ut} tömb lenne, de ${leiro(x)}.`);
      x.forEach((e, i) => { if (typeof e !== 'string') throw new MinosegAlakHiba(`${elorag}: a(z) ${ut}[${i}] szöveg lenne, de ${leiro(e)}.`); });
      return;
    }
    const nullable = alak.endsWith('?');
    if (x === null && nullable) return;
    const tipus = alak.startsWith('szam') ? 'number' : 'string';
    if (typeof x === tipus && (tipus !== 'number' || Number.isFinite(x))) return;
    throw new MinosegAlakHiba(`${elorag}: a(z) ${ut} ${tipus === 'number' ? 'szám' : 'szöveg'}${nullable ? ' vagy null' : ''} lenne, de ${leiro(x)}.`);
  }
  if (Array.isArray(alak)) {
    if (!Array.isArray(x)) throw new MinosegAlakHiba(`${elorag}: a(z) ${ut} tömb lenne, de ${leiro(x)}.`);
    x.forEach((elem, i) => ellenorzes(elem, alak[0], `${ut}[${i}]`));
    return;
  }
  if (typeof x !== 'object' || x === null || Array.isArray(x)) {
    throw new MinosegAlakHiba(`${elorag}: a(z) ${ut || 'válasz'} objektum lenne, de ${leiro(x)}.`);
  }
  for (const [kulcs, al] of Object.entries(alak)) {
    const kulcsUt = ut ? `${ut}.${kulcs}` : kulcs;
    if (!(kulcs in x)) throw new MinosegAlakHiba(`${elorag}: hiányzik a(z) ${kulcsUt}.`);
    ellenorzes((x as Record<string, unknown>)[kulcs], al, kulcsUt);
  }
}

/** Ellenőrzi, hogy a `Json` válasz megfelel-e a szerződésnek; különben érthető hibát dob. */
export function ellenorizMinoseg(valasz: unknown): MinosegValasz {
  if (typeof valasz !== 'object' || valasz === null || Array.isArray(valasz)) {
    throw new MinosegAlakHiba(`A minőségi mutatók válasza nem objektum (${leiro(valasz)}). Frissítsd az oldalt; ha ismétlődik, szólj nekünk.`);
  }
  ellenorzes(valasz, ALAK, '');
  return valasz as MinosegValasz;
}
